import React, { useState } from "react";
import { AiMaterial } from "../../types/Ai";
import classes from "./assistant.module.scss";

interface AskMaterialChipProps {
    material: AiMaterial;
    /** The question's id: keeps the toggle's id unique in the thread. */
    messageId: number;
    /** Change whether the file's amounts stay visible to the AI (PUT …/material); left out when it can't be changed. */
    onPublicFigures?: (publicFigures: boolean) => Promise<boolean>;
}

const FIGURES_HELP = "Public material such as a published survey or a web page: its amounts stay visible so the AI can compare them. Keep them hidden for anything with our or a client's money.";

/**
 * The file or long paste on a question ("survey.pdf · 12 pages", "Pasted text · 1,842 characters") and,
 * right beside it, a one-click toggle for whether its figures are visible to the AI. Web pages are public,
 * so theirs is always on and they open in a new tab.
 */
const AskMaterialChip: React.FC<AskMaterialChipProps> = ({ material, messageId, onPublicFigures }) => {
    const [isSaving, setIsSaving] = useState(false);
    const isWeb = material.kind === "web";
    const isVisible = isWeb || !!material.publicFigures;
    const label = `${material.name} · ${material.parts}${material.truncated ? " · first part only" : ""}`;
    const toggle = async () => {
        if (!onPublicFigures || isSaving || isWeb) return;
        setIsSaving(true);
        await onPublicFigures(!isVisible);
        setIsSaving(false);
    };
    const chip = (
        <>
            <i className={`bi ${isWeb ? "bi-globe" : "bi-file-earmark-text"}`} aria-hidden="true" /> {label}
        </>
    );
    return (
        <div className={classes.material}>
            {material.sourceUrl
                ? <a href={material.sourceUrl} target="_blank" rel="noopener noreferrer" className={classes.fileChip} title="Open the web page this came from, in a new tab">{chip}</a>
                : <span className={classes.fileChip} title={`${label}: read whole by CRX; names, emails and phones are hidden from the AI`}>{chip}</span>}
            <button type="button" id={`material-public-${messageId}`} className={`btn-unstyled ${classes.figuresToggle} ${isVisible ? classes.figuresOn : ""}`}
                aria-pressed={isVisible} disabled={isWeb || isSaving || !onPublicFigures} onClick={toggle}
                title={isWeb ? "A public web page: its figures are always visible to the AI" : `${isVisible ? "Click to hide" : "Click to show"} this file's figures to the AI. ${FIGURES_HELP}`}>
                <i className={`bi ${isVisible ? "bi-eye" : "bi-eye-slash"}`} aria-hidden="true" /> {isVisible ? "Figures visible to the AI" : "Figures hidden from the AI"}
            </button>
        </div>
    );
};

export default AskMaterialChip;
